import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Eye, EyeOff, Download, Smartphone, Share, MoreVertical } from "lucide-react";

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // PWA Install prompt handler
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      // Si hay prompt disponible, instalar automáticamente
      installPrompt.prompt();
      await installPrompt.userChoice;
      // No limpiamos el prompt para que el botón siga funcionando
    } else {
      // Solo mostrar instrucciones si no hay prompt disponible
      setShowInstallHelp(true);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Redirect based on role
      if (user.role === 'proveedor') {
        navigate("/supplier");
      } else {
        navigate("/");
      }
    }
  }, [user, navigate]);

  // Don't render login form if user is logged in
  if (user) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
        toast.success("¡Bienvenido!");
      } else {
        result = await register(name, email, password);
        toast.success("¡Cuenta creada exitosamente!");
      }
      // Redirect based on role
      if (result?.user?.role === 'proveedor') {
        navigate("/supplier");
      } else {
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error en la autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FFF5F5]">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="text-center">
            <img 
              src="https://hosting.photobucket.com/0527623e-3ddc-48a1-b0eb-b299d3c19e95/b8990f2e-2369-4dcc-8414-e8227f89b88d.png" 
              alt="Darkar Co. Logo"
              className="w-32 h-32 object-contain mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-[#4A4A4A] font-['Manrope']">
              Darkar Co.
            </h1>
            <p className="mt-1 text-[#6B7280]">
              Control de Ventas
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {!isLogin && (
                <div className="space-y-2 animate-fade-in">
                  <Label htmlFor="name" className="text-[#4A4A4A]">Nombre</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Tu nombre"
                    required={!isLogin}
                    data-testid="register-name-input"
                    className="bg-white border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF] focus:border-[#F43F5E] focus:ring-[#F43F5E]"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#4A4A4A]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  data-testid="login-email-input"
                  className="bg-white border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF] focus:border-[#F43F5E] focus:ring-[#F43F5E]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#4A4A4A]">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                    className="bg-white border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF] focus:border-[#F43F5E] focus:ring-[#F43F5E] pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-4 h-4"></div>
                  Procesando...
                </span>
              ) : (
                isLogin ? "Iniciar Sesión" : "Crear Cuenta"
              )}
            </Button>
          </form>

          {/* Toggle */}
          <div className="text-center">
            <p className="text-[#6B7280]">
              {isLogin ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
                className="ml-2 text-[#F43F5E] hover:text-[#E11D48] font-medium"
              >
                {isLogin ? "Regístrate" : "Inicia Sesión"}
              </button>
            </p>
          </div>
          
          {/* Install App Button - Always visible */}
          <div className="mt-6 pt-6 border-t border-[#FEE2E2]">
            <Button
              type="button"
              onClick={handleInstallClick}
              data-testid="install-app-login-button"
              className="w-full bg-gradient-to-r from-[#F43F5E] to-[#EC4899] hover:from-[#E11D48] hover:to-[#DB2777] text-white shadow-lg"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Instalar App en tu dispositivo
            </Button>
            <p className="text-center text-xs text-[#6B7280] mt-2">
              Accede más rápido desde tu pantalla de inicio
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-[#F43F5E] py-4 text-center">
        <p className="text-white text-sm">
          Copyright © {new Date().getFullYear()} Darkar Company Studios, LLC. 🍦
        </p>
      </footer>

      {/* Install Help Dialog */}
      <Dialog open={showInstallHelp} onOpenChange={setShowInstallHelp}>
        <DialogContent className="bg-white border-[#FECDD3] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#4A4A4A]">
              <Smartphone className="w-5 h-5 text-[#F43F5E]" />
              Instalar Darkar App
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#6B7280] text-sm">
              Instala la app en tu dispositivo para acceder más rápido:
            </p>
            
            <div className="space-y-3">
              <div className="bg-[#FEE2E2] rounded-lg p-3">
                <p className="font-medium text-[#4A4A4A] text-sm flex items-center gap-2">
                  <span className="bg-[#F43F5E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                  Chrome / Edge (Android/PC)
                </p>
                <p className="text-[#6B7280] text-xs mt-1 ml-7">
                  Toca el menú <MoreVertical className="w-3 h-3 inline" /> → "Instalar app" o "Agregar a inicio"
                </p>
              </div>
              
              <div className="bg-[#FEE2E2] rounded-lg p-3">
                <p className="font-medium text-[#4A4A4A] text-sm flex items-center gap-2">
                  <span className="bg-[#F43F5E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                  Safari (iPhone/iPad)
                </p>
                <p className="text-[#6B7280] text-xs mt-1 ml-7">
                  Toca <Share className="w-3 h-3 inline" /> Compartir → "Agregar a pantalla de inicio"
                </p>
              </div>
            </div>

            <Button 
              onClick={() => setShowInstallHelp(false)}
              className="w-full bg-[#F43F5E] hover:bg-[#E11D48] text-white"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
